import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loading } from '@/components/ui/loading';
import { ScenarioRunFlow } from '@/components/eval-studio/ScenarioRunFlow';
import { getScenarioRunObservabilityLogIds } from '@/components/eval-studio/scenarioFlowSteps';
import {
  useEvalStudioScenarioRunRetrieve,
  useEvalStudioScenarioRunTrace,
} from '@/components/eval-studio/evalStudioApi';
import { fetchObservabilityLogsBatch, getObservabilityLogsBatchQueryKey } from '@/api/observabilityLogs';
import type { ObservabilityLogsRetrieve200ResultsItem } from '@/gen-api/models';
import type { ScenarioRunSummary } from '@/types/eval-studio';

type ScenarioRunFlowDialogProps = {
  run: ScenarioRunSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ScenarioRunFlowDialog({ run, open, onOpenChange }: ScenarioRunFlowDialogProps) {
  const scenarioRunId = open && run ? run.id : null;
  const runQuery = useEvalStudioScenarioRunRetrieve(scenarioRunId);
  const traceQuery = useEvalStudioScenarioRunTrace(scenarioRunId);
  const trace = traceQuery.data;

  const logIds = useMemo(() => getScenarioRunObservabilityLogIds(trace), [trace]);
  const logsQuery = useQuery({
    queryKey: getObservabilityLogsBatchQueryKey(logIds, 'CHAT', true),
    queryFn: ({ signal }) => fetchObservabilityLogsBatch(logIds, { collection: 'CHAT', stable: true, signal }),
    enabled: Boolean(open && logIds.length),
    retry: false,
  });

  const logsById = useMemo(() => {
    const map = new Map<string, ObservabilityLogsRetrieve200ResultsItem>();
    for (const item of logsQuery.data?.results ?? []) {
      if (item?.id) {
        map.set(item.id, item as unknown as ObservabilityLogsRetrieve200ResultsItem);
      }
    }
    return map;
  }, [logsQuery.dataUpdatedAt, logsQuery.data]);

  const traceLoading = traceQuery.isLoading && traceQuery.fetchStatus !== 'idle';
  const logsLoading = logsQuery.isLoading && logsQuery.fetchStatus !== 'idle';
  const loading = runQuery.isLoading || traceLoading || logsLoading;
  const detail = runQuery.data;
  const summary = detail ?? run;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Flow Chart</DialogTitle>
          <DialogDescription>Step-by-step view of this scenario run.</DialogDescription>
        </DialogHeader>
        <div className="mt-4">
          {!summary ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No run data available</div>
          ) : loading ? (
            <div className="flex items-center justify-center p-6">
              <Loading />
            </div>
          ) : (
            <ScenarioRunFlow run={summary} trace={trace} detail={detail} logsById={logsById} height={600} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
